import type { Section } from '../../../../../../api/types/expenseareas';

/** Преобразует errors с бека (по индексам в payload) в ключи по id поля / localId секции. */
export function normalizeSpqBackendValidationErrors(
    raw: Record<string, string[]> | undefined,
    uiSections: Section[]
): Record<string, string[]> {
    const out: Record<string, string[]> = {};
    if (!raw) return out;

    for (const [path, messages] of Object.entries(raw)) {
        const optMatch = path.match(/^sections\.(\d+)\.fields\.(\d+)\.options\.(\d+)\.(.+)$/);
        if (optMatch) {
            const si = Number(optMatch[1]);
            const fi = Number(optMatch[2]);
            const oi = Number(optMatch[3]);
            const attr = optMatch[4];
            const fieldId = uiSections[si]?.fields?.[fi]?.id;
            if (fieldId != null && fieldId !== '') {
                out[`option:${String(fieldId)}:${oi}:${attr}`] = messages;
            } else {
                out[path] = messages;
            }
            continue;
        }

        const fieldMatch = path.match(/^sections\.(\d+)\.fields\.(\d+)\.(.+)$/);
        if (fieldMatch) {
            const si = Number(fieldMatch[1]);
            const fi = Number(fieldMatch[2]);
            const attr = fieldMatch[3];
            const fieldId = uiSections[si]?.fields?.[fi]?.id;
            if (fieldId != null && fieldId !== '') {
                out[`field:${String(fieldId)}:${attr}`] = messages;
            } else {
                out[path] = messages;
            }
            continue;
        }

        const secMatch = path.match(/^sections\.(\d+)\.([^\\.]+)$/);
        if (secMatch && secMatch[2] !== 'fields') {
            const si = Number(secMatch[1]);
            const attr = secMatch[2];
            const localId = uiSections[si]?.localId;
            if (localId != null && localId !== '') {
                out[`section:${String(localId)}:${attr}`] = messages;
            } else {
                out[path] = messages;
            }
            continue;
        }

        out[path] = messages;
    }
    return out;
}

export function parseApiValidationPayload(err: unknown): {
    rawErrors?: Record<string, string[]>;
    message?: string;
} {
    if (!err || typeof err !== 'object') return {};
    const e = err as { data?: unknown };
    const data = e.data;
    if (!data || typeof data !== 'object') return {};

    const d = data as { message?: unknown; errors?: unknown };
    const message = typeof d.message === 'string' ? d.message : undefined;

    let rawErrors: Record<string, string[]> | undefined;
    if (d.errors && typeof d.errors === 'object' && !Array.isArray(d.errors)) {
        rawErrors = {};
        for (const [k, v] of Object.entries(d.errors as Record<string, unknown>)) {
            if (Array.isArray(v)) {
                rawErrors[k] = v.filter((x): x is string => typeof x === 'string');
            } else if (typeof v === 'string') {
                rawErrors[k] = [v];
            }
        }
    }

    return { rawErrors, message };
}

export function collectSpqBackendErrorFieldIds(errors: Record<string, string[]>): string[] {
    const ids = new Set<string>();
    for (const k of Object.keys(errors)) {
        const opt = k.match(/^option:([^:]+):/);
        if (opt) ids.add(opt[1]);
        const fld = k.match(/^field:([^:]+):/);
        if (fld) ids.add(fld[1]);
    }
    return [...ids];
}

export function getSpqBackendMessagesForField(
    errors: Record<string, string[]>,
    fieldId: string | number | null | undefined
): string[] {
    if (fieldId == null) return [];
    const id = String(fieldId);
    const msgs: string[] = [];
    for (const [k, arr] of Object.entries(errors)) {
        if (k.startsWith(`field:${id}:`) || k.startsWith(`option:${id}:`)) {
            msgs.push(...arr);
        }
    }
    return msgs;
}

export function getSpqBackendOptionMessages(
    errors: Record<string, string[]>,
    fieldId: string | number | null | undefined,
    optionIndex: number,
    attribute: string
): string[] {
    if (fieldId == null) return [];
    const key = `option:${String(fieldId)}:${optionIndex}:${attribute}`;
    return errors[key] ?? [];
}

export function getSpqBackendFieldAttributeMessages(
    errors: Record<string, string[]>,
    fieldId: string | number | null | undefined,
    attribute: string
): string[] {
    if (fieldId == null) return [];
    const key = `field:${String(fieldId)}:${attribute}`;
    return errors[key] ?? [];
}

export function getSpqBackendSectionMessages(
    errors: Record<string, string[]>,
    sectionLocalId: string | number | null | undefined,
    attribute?: string
): string[] {
    if (sectionLocalId == null) return [];
    const prefix = `section:${String(sectionLocalId)}:`;
    const msgs: string[] = [];
    for (const [k, arr] of Object.entries(errors)) {
        if (!k.startsWith(prefix)) continue;
        if (attribute != null && !k.startsWith(`${prefix}${attribute}`)) continue;
        msgs.push(...arr);
    }
    return msgs;
}
